/**
 * Camada 1 · O SELO DE ESTADO.
 *
 * ============================================================================
 * POR QUE ELE NASCEU, e a causa foi medida
 * ============================================================================
 * A varredura de cor de 09/09/2026, pedida pelo fundador com as palavras "REVEJA TUDO E
 * COLOQUE TUDO SUAVE NA PLATAFORMA TODA", nomeou a causa raiz de ele ter reclamado de cor
 * gritante CINCO vezes seguidas: **nao existia componente de selo**.
 *
 * O mesmo markup estava copiado em SETE lugares (`Recepcao`, `Painel`, `Financeiro`,
 * `Minutas`, `Reembolso`, `Agenda` e `DueList`), cada um com o proprio mapa de cor. Enquanto
 * cada tela escrevesse `bg-<estado>-suave` a mao, nenhuma mudanca de token as alcancaria de
 * forma uniforme, e "padrao da plataforma" significaria "padronizado onde alguem lembrou".
 *
 * ============================================================================
 * POR QUE O SELO CONTINUA COM COR CHEIA, e isto NAO contradiz o vidro
 * ============================================================================
 * A regra do vidro desta casa decide pelo TAMANHO da superficie, e nao pela cor. O selo tem
 * uns 20 pixels de altura, e ali a tinta com alpha o APAGA.
 *
 * ISSO FOI APRENDIDO NA TELA E CUSTOU CARO: em 09/09/2026 eu clareei os tokens `-suave` para
 * suavizar uma FAIXA grande, e os mesmos tokens pintavam o selo. Ele recarregou e respondeu
 * "E PIOR QUE ESTA PIOR PRA ENXERGAR". Um token servindo a dois tamanhos sempre erra em um
 * dos dois, e a saida foi separar: a faixa virou vidro, o selo ficou com o tom cheio.
 *
 * ============================================================================
 * O QUE ELE RESOLVE ALEM DE CENTRALIZAR
 * ============================================================================
 * O CONTRASTE, que estava abaixo do piso em dois estados. A varredura mediu `text-info`
 * sobre `bg-info-suave` em **4,07** no tema escuro, contra os 4,5 do WCAG. Aqui a cor do
 * texto e escolhida junto com a do fundo, num lugar so, e passa a ser conferivel.
 */
import type { ReactNode } from 'react';

/**
 * O TOM diz o SIGNIFICADO, e nunca a cor.
 *
 * Uma tela que pedisse `cor="amarelo"` amarraria o significado a paleta, e trocar a paleta
 * mudaria o que a tela quis dizer. `pendente` continua sendo pendente em qualquer tema.
 */
export type TomDoSelo = 'neutro' | 'marca' | 'sucesso' | 'alerta' | 'erro' | 'info';

export interface PropsSelo {
  readonly tom?: TomDoSelo;
  readonly children: ReactNode;
  /**
   * Impede o selo de encolher dentro de um flex.
   *
   * PADRAO `true` porque o caso comum e o selo dividir a linha com um NOME de paciente, que
   * e texto longo e faminto por espaco. Sem isto, o selo aparece cortado no celular, que e
   * onde a linha e mais estreita.
   */
  readonly naoEncolhe?: boolean;
}

/**
 * A cor de cada tom, e as DUAS metades (fundo e texto) sao escolhidas juntas.
 *
 * `text-<estado>` E NAO `text-<estado>-forte`: sobre o fundo `-suave`, que e claro no tema
 * claro e escuro no tema escuro, a cor cheia do estado e a que da contraste nos dois. Esta
 * dupla e a que a varredura mediu.
 */
const POR_TOM: Readonly<Record<TomDoSelo, string>> = {
  neutro: 'bg-neutro-suave text-texto-suave',
  marca: 'bg-marca-suave text-marca',
  sucesso: 'bg-sucesso-suave text-sucesso',
  alerta: 'bg-alerta-suave text-alerta',
  erro: 'bg-erro-suave text-erro',
  info: 'bg-info-suave text-info',
};

/**
 * Um selo de estado.
 *
 * ELE NAO E CLICAVEL, e nunca deve ser: selo comunica um estado que o sistema decidiu, e nao
 * uma acao que a pessoa pode tomar. Onde o estado precisar virar filtro, quem clica e o
 * cartao ou a linha em volta, e nao o selo, senao a pessoa passa a mirar num alvo de 20px
 * quando ha um de 300px disponivel.
 */
export function Selo({ tom = 'neutro', children, naoEncolhe = true }: PropsSelo) {
  return (
    <span
      className={[
        // `inline-flex` e nao `inline-block`: com `inline-block` o texto do selo herda a
        // linha-base do texto em volta e o selo desalinha por um ou dois pixels ao lado de
        // um titulo maior.
        'inline-flex items-center rounded-cx-full px-cx-2 py-cx-1 text-cx-xs font-medium',
        naoEncolhe ? 'shrink-0' : '',
        POR_TOM[tom],
      ].join(' ')}
    >
      {children}
    </span>
  );
}
